// P1 (2026-07-12) — Dashboard tile drill-down pages.
//
// The Dashboard cards used to navigate to /transactions (a generic list
// mixing every transaction type). Each tile now opens a dedicated view:
//
//   /dashboard/expenses          — deep breakdown (donut + accordion)
//   /dashboard/income            — same shape, income side
//   /dashboard/net-balance       — P&L summary (income − expenses)
//   /dashboard/cash              — wraps Cashbook filtered to 1000/1100
//   /dashboard/bank-balance      — wraps the Bank page
//   /dashboard/payables          — wraps the Payables page filtered open
//   /dashboard/receivables       — wraps the Receivables page filtered open
//   /dashboard/payroll-pending   — wraps Payroll filtered draft/approved
//
// Backend endpoints:
//   GET /api/dashboard/expenses-breakdown[?year=YYYY|?from=YYYY-MM-DD&to=...]
//   GET /api/dashboard/income-breakdown   (same shape, credit side)
//   GET /api/dashboard/net-balance-summary

import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ChevronDown, ChevronRight, ArrowLeft, Loader2, TrendingDown, TrendingUp, Wallet } from "lucide-react";
import { PieChart, Pie, Cell, Tooltip as ReTooltip, ResponsiveContainer, Legend } from "recharts";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
// Bug bundle #2/#2 (2026-08-10) — Reuse the JE detail modal from
// TransactionsPage so drill-down JE clicks open the same read-only Dr/Cr
// layout the user sees on the Transactions list.
import { TxDetailModal } from "@/pages/TransactionsPage";

const fmtUSD = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;

const PIE_COLORS = [
  "#4FA3D1", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6",
  "#06b6d4", "#ec4899", "#84cc16", "#a855f7", "#0ea5e9",
  "#f97316", "#10b981", "#6366f1", "#d946ef",
];

function PeriodPicker({ year, setYear }) {
  const years = [2024, 2025, 2026, 2027];
  return (
    <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
      <SelectTrigger className="w-32" data-testid="period-year-picker">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {years.map((y) => (
          <SelectItem key={y} value={String(y)}>{y}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function BackToDash() {
  return (
    <Link
      to="/dashboard"
      className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#4FA3D1]"
      data-testid="back-to-dashboard"
    >
      <ArrowLeft className="h-3.5 w-3.5" /> Back to Dashboard
    </Link>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Reusable: breakdown view (used by Expenses + Income)
// ─────────────────────────────────────────────────────────────────────────────

function periodToRange(period, year) {
  const today = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  const y = Number(year) || today.getFullYear();
  const isCurrentYear = y === today.getFullYear();
  const p = (period || "ytd").toLowerCase();
  if (p === "mtd" && isCurrentYear) {
    return { from: iso(new Date(today.getFullYear(), today.getMonth(), 1)), to: iso(today) };
  }
  if (p === "qtd" && isCurrentYear) {
    const qMonth = 3 * Math.floor(today.getMonth() / 3);
    return { from: iso(new Date(today.getFullYear(), qMonth, 1)), to: iso(today) };
  }
  if (p === "ytd" && isCurrentYear) {
    return { from: `${y}-01-01`, to: iso(today) };
  }
  // Full year for previous years or explicit "year" mode
  return { from: `${y}-01-01`, to: `${y}-12-31` };
}

function BreakdownView({ title, accent, icon: Icon, totalLabel, totalKey,
                         endpoint }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialYear = Number(searchParams.get("year")) || new Date().getFullYear();
  const initialPeriod = (searchParams.get("period") || "ytd").toLowerCase();
  const [year, setYear] = useState(initialYear);
  const [period, setPeriod] = useState(["mtd", "qtd", "ytd"].includes(initialPeriod) ? initialPeriod : "ytd");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  // Bug bundle #2/#2 (2026-08-10) — clicked JE Reference opens the
  // TxDetailModal (read-only Dr/Cr view reused from TransactionsPage).
  const [openJeId, setOpenJeId] = useState(null);

  const { from, to } = periodToRange(period, year);

  useEffect(() => {
    // Keep URL in sync so refresh / share preserves state.
    const next = new URLSearchParams(searchParams);
    next.set("period", period); next.set("year", String(year));
    setSearchParams(next, { replace: true });
  }, [period, year, searchParams, setSearchParams]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`${endpoint}?from=${from}&to=${to}`);
        if (!cancelled) setData(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [endpoint, from, to]);

  const pieData = useMemo(() => {
    if (!data) return [];
    // P0 (2026-07-12) — hide $0 / empty categories from chart + table.
    // Bug bundle #2/#2 — also gate on `line_count` because
    // `transactions[]` may be 0 for payroll accrual categories that
    // have real JE lines.
    return (data.categories || [])
      .filter((c) => (c.total || 0) > 0.005 || (c.line_count || 0) > 0 || (c.transactions?.length || 0) > 0)
      .map((c, i) => ({
        name: `${c.category_code} ${c.category_name}`,
        value: c.total,
        color: PIE_COLORS[i % PIE_COLORS.length],
      }));
  }, [data]);

  const visibleCategories = useMemo(() => {
    if (!data) return [];
    return (data.categories || []).filter(
      (c) => (c.total || 0) > 0.005 || (c.line_count || 0) > 0 || (c.transactions?.length || 0) > 0,
    );
  }, [data]);

  return (
    <div className="space-y-5" data-testid={`drilldown-${totalKey}`}>
      <div>
        <BackToDash />
        <div className="flex items-end justify-between mt-2">
          <div>
            <div className="flex items-center gap-2">
              <Icon className={`h-5 w-5 ${accent}`} />
              <h1 className="text-2xl font-bold text-slate-900">{title}</h1>
            </div>
            <p className="mt-1 text-sm text-slate-500">
              {totalLabel}:{" "}
              <span
                className="text-lg font-semibold text-slate-900 tabular-nums"
                data-testid={`drilldown-total-${totalKey}`}
              >
                {data ? fmtUSD(data[totalKey]) : "—"}
              </span>{" "}
              · {period.toUpperCase()} · {year}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div
              className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5"
              data-testid="drilldown-period-toggle"
            >
              {["mtd", "qtd", "ytd"].map((p) => (
                <button
                  key={p}
                  onClick={() => setPeriod(p)}
                  className={`px-3 h-7 text-xs font-medium rounded-md transition-colors ${
                    period === p
                      ? "bg-[#E7EEFB] text-[#1F4FA3]"
                      : "text-slate-600 hover:bg-slate-50"
                  }`}
                  data-testid={`drilldown-period-${p}`}
                >
                  {p.toUpperCase()}
                </button>
              ))}
            </div>
            <PeriodPicker year={year} setYear={setYear} />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="animate-spin h-6 w-6 text-[#4FA3D1]" />
        </div>
      ) : !data || data.categories.length === 0 ? (
        <Card><CardContent className="py-16 text-center text-slate-500">
          No data for {year}.
        </CardContent></Card>
      ) : (
        <>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm font-semibold text-slate-700 mb-2">
                Distribution by account
              </div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      innerRadius={55}
                      outerRadius={95}
                      paddingAngle={2}
                      stroke="#fff"
                    >
                      {pieData.map((d) => <Cell key={d.name} fill={d.color} />)}
                    </Pie>
                    <ReTooltip formatter={(v) => fmtUSD(v)} />
                    <Legend
                      verticalAlign="middle" align="right" layout="vertical"
                      wrapperStyle={{ fontSize: 11 }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-0">
              <Table data-testid="breakdown-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"></TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right">Share</TableHead>
                    <TableHead className="text-right"># txns</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.categories.map((c, i) => {
                    if ((c.total || 0) <= 0.005
                        && (c.line_count || 0) === 0
                        && (c.transactions?.length || 0) === 0) return null;
                    const open = !!expanded[c.category_code];
                    const jeLines = c.je_lines || [];
                    return (
                      <React.Fragment key={c.category_code}>
                        <TableRow
                          data-testid={`cat-row-${c.category_code}`}
                          onClick={() => setExpanded((m) => ({
                            ...m, [c.category_code]: !open,
                          }))}
                          className="cursor-pointer hover:bg-slate-50"
                        >
                          <TableCell>
                            {open
                              ? <ChevronDown className="h-4 w-4" />
                              : <ChevronRight className="h-4 w-4" />}
                          </TableCell>
                          <TableCell className="font-mono text-xs">{c.category_code}</TableCell>
                          <TableCell>
                            <span
                              className="inline-block h-2.5 w-2.5 rounded-sm mr-2 align-middle"
                              style={{ background: PIE_COLORS[i % PIE_COLORS.length] }}
                            />
                            {c.category_name}
                          </TableCell>
                          <TableCell className="text-right tabular-nums font-semibold">
                            {fmtUSD(c.total)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {c.share_pct.toFixed(1)}%
                          </TableCell>
                          <TableCell
                            className="text-right tabular-nums"
                            data-testid={`cat-count-${c.category_code}`}
                          >
                            {/* Bug bundle #2/#2 — real count of JE lines
                                contributing to the category total (was
                                the count of linked *transactions*, which
                                was 0 for payroll accruals / manual JEs). */}
                            {typeof c.line_count === "number"
                              ? c.line_count
                              : (c.transactions?.length || 0)}
                          </TableCell>
                        </TableRow>
                        {open && (
                          <TableRow>
                            <TableCell colSpan={6} className="bg-slate-50 p-0">
                              <div className="px-4 py-3"
                                   data-testid={`cat-expanded-${c.category_code}`}>
                                {jeLines.length === 0 ? (
                                  <div className="text-xs text-slate-500 py-2">
                                    No JE lines contributed to this category
                                    inside the selected period.
                                  </div>
                                ) : (
                                  <>
                                    {c.je_lines_truncated && (
                                      <div
                                        className="mb-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1"
                                        data-testid={`cat-truncated-${c.category_code}`}
                                      >
                                        Showing the first {jeLines.length} of
                                        {" "}{c.line_count.toLocaleString()} JE lines.
                                        Refine the period to see the rest.
                                      </div>
                                    )}
                                    <Table>
                                      <TableHeader>
                                        <TableRow>
                                          <TableHead>Date</TableHead>
                                          <TableHead>JE Reference</TableHead>
                                          <TableHead>Narration</TableHead>
                                          <TableHead>Counterparty</TableHead>
                                          <TableHead className="text-right">Debit</TableHead>
                                          <TableHead className="text-right">Credit</TableHead>
                                          <TableHead>Contra Account</TableHead>
                                          <TableHead className="text-right">Running total</TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {(() => {
                                          let running = 0;
                                          return jeLines.map((ln, idx) => {
                                            running += Number(ln.debit || 0) - Number(ln.credit || 0);
                                            const contras = ln.contra_lines || [];
                                            // Full string for tooltip
                                            const contraFull = contras.map((cl) => {
                                              const side = Number(cl.debit) > 0 ? "Dr" : "Cr";
                                              const amt = Number(cl.debit) > 0 ? cl.debit : cl.credit;
                                              return `${side} ${cl.code} ${cl.name} ${fmtUSD(amt)}`;
                                            }).join("; ");
                                            // Compact display: first 2 lines, "+N more" if longer
                                            const shortItems = contras.slice(0, 2).map((cl) => {
                                              const side = Number(cl.debit) > 0 ? "Dr" : "Cr";
                                              const amt = Number(cl.debit) > 0 ? cl.debit : cl.credit;
                                              return `${side} ${cl.code} ${cl.name.split(/[\s—-]/)[0]} ${fmtUSD(amt)}`;
                                            }).join("; ");
                                            const moreCount = contras.length > 2 ? contras.length - 2 : 0;
                                            return (
                                              <TableRow
                                                key={`${c.category_code}-ln-${idx}`}
                                                data-testid={`cat-line-${c.category_code}-${idx}`}
                                              >
                                                <TableCell className="text-xs">{ln.entry_date}</TableCell>
                                                <TableCell className="font-mono text-xs">
                                                  <button
                                                    type="button"
                                                    className="text-[#4FA3D1] hover:underline"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setOpenJeId(ln.je_id);
                                                    }}
                                                    title={ln.je_id}
                                                    data-testid={`cat-je-open-${c.category_code}-${idx}`}
                                                  >
                                                    {ln.reference || ln.je_id.slice(0, 8) + "…"}
                                                  </button>
                                                </TableCell>
                                                <TableCell className="text-xs max-w-[280px]">
                                                  <span title={ln.narration} className="line-clamp-2">
                                                    {ln.narration || "—"}
                                                  </span>
                                                </TableCell>
                                                <TableCell className="text-xs">
                                                  {ln.counterparty_name || "—"}
                                                </TableCell>
                                                <TableCell className="text-right tabular-nums text-xs">
                                                  {Number(ln.debit) > 0 ? fmtUSD(ln.debit) : ""}
                                                </TableCell>
                                                <TableCell className="text-right tabular-nums text-xs">
                                                  {Number(ln.credit) > 0 ? fmtUSD(ln.credit) : ""}
                                                </TableCell>
                                                <TableCell
                                                  className="text-xs max-w-[260px]"
                                                  data-testid={`cat-contra-${c.category_code}-${idx}`}
                                                >
                                                  {contras.length === 0 ? (
                                                    <span className="text-slate-400">—</span>
                                                  ) : (
                                                    <span
                                                      title={contraFull}
                                                      className="text-slate-700"
                                                    >
                                                      {shortItems}
                                                      {moreCount > 0 && (
                                                        <span className="ml-1 text-slate-500">
                                                          … +{moreCount} more
                                                        </span>
                                                      )}
                                                    </span>
                                                  )}
                                                </TableCell>
                                                <TableCell className="text-right tabular-nums text-xs font-semibold">
                                                  {fmtUSD(running)}
                                                </TableCell>
                                              </TableRow>
                                            );
                                          });
                                        })()}
                                      </TableBody>
                                    </Table>
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
      {/* Bug bundle #2/#2 (2026-08-10) — JE detail modal instance.
          Opens when a JE Reference is clicked inside a category expand. */}
      {openJeId && (
        <TxDetailModal jeId={openJeId} onClose={() => setOpenJeId(null)} />
      )}
    </div>
  );
}

export function ExpensesBreakdownPage() {
  return <BreakdownView
    title="Total Expenses — Breakdown"
    accent="text-red-600"
    icon={TrendingDown}
    totalLabel="Total"
    totalKey="total_expenses"
    endpoint="/dashboard/expenses-breakdown"
  />;
}

export function IncomeBreakdownPage() {
  return <BreakdownView
    title="Total Income — Breakdown"
    accent="text-emerald-600"
    icon={TrendingUp}
    totalLabel="Total"
    totalKey="total_income"
    endpoint="/dashboard/income-breakdown"
  />;
}

// ─────────────────────────────────────────────────────────────────────────────
// Net balance summary (minimal)
// ─────────────────────────────────────────────────────────────────────────────

export function NetBalanceDrillDownPage() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/dashboard/net-balance-summary?year=${year}`);
        setData(data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [year]);

  return (
    <div className="space-y-5" data-testid="drilldown-net-balance">
      <BackToDash />
      <div className="flex items-end justify-between mt-2">
        <div className="flex items-center gap-2">
          <Wallet className="h-5 w-5 text-[#4FA3D1]" />
          <h1 className="text-2xl font-bold">Net Balance — P&amp;L Summary</h1>
        </div>
        <PeriodPicker year={year} setYear={setYear} />
      </div>
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin h-6 w-6 text-[#4FA3D1]" /></div>
      ) : !data ? null : (
        <div className="grid md:grid-cols-3 gap-4">
          <Card><CardContent className="p-5">
            <p className="text-xs uppercase tracking-wider text-emerald-700">Total Income</p>
            <p className="mt-2 text-2xl font-bold tabular-nums">{fmtUSD(data.total_income)}</p>
            <p className="mt-1 text-xs text-slate-500">{data.income_top.length} income categories</p>
          </CardContent></Card>
          <Card><CardContent className="p-5">
            <p className="text-xs uppercase tracking-wider text-red-700">Total Expenses</p>
            <p className="mt-2 text-2xl font-bold tabular-nums">{fmtUSD(data.total_expenses)}</p>
            <p className="mt-1 text-xs text-slate-500">{data.expenses_top.length} expense categories</p>
          </CardContent></Card>
          <Card><CardContent className="p-5">
            <p className="text-xs uppercase tracking-wider text-[#4FA3D1]">Net Balance</p>
            <p className={`mt-2 text-2xl font-bold tabular-nums ${data.net_balance >= 0 ? "text-emerald-700" : "text-red-700"}`}>
              {fmtUSD(data.net_balance)}
            </p>
            <p className="mt-1 text-xs text-slate-500">Income − Expenses · {year}</p>
          </CardContent></Card>
        </div>
      )}
      <div className="flex gap-3">
        <Link to="/dashboard/income"><Button variant="outline">Open Income Breakdown</Button></Link>
        <Link to="/dashboard/expenses"><Button variant="outline">Open Expenses Breakdown</Button></Link>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase 31 (2026-07-17) — Drill-down shells that used to `_Redirect` are
// now proper pages: they render a "Back to Dashboard" breadcrumb, a
// short summary, and a forward link into the working feature page.
// This gives every dashboard tile a landing surface with back-navigation
// (previously the redirect stubs bounced the user straight into
// /cashbook, /bank, /payables etc with no way back).
// ─────────────────────────────────────────────────────────────────────────────

import { Card as _Card, CardContent as _CardContent } from "@/components/ui/card";
import { Button as _Button } from "@/components/ui/button";

function _DrillShell({ title, description, forwardLabel, forwardTo, testid }) {
  return (
    <div className="space-y-4" data-testid={testid}>
      <BackToDash />
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">{title}</h2>
          <p className="text-sm text-slate-500 mt-1">{description}</p>
        </div>
      </div>
      <_Card className="border border-slate-200">
        <_CardContent className="p-6">
          <p className="text-sm text-slate-600 mb-4">
            Continue to the full page for the complete list, filters, and actions.
          </p>
          <Link to={forwardTo}>
            <_Button className="bg-[#4FA3D1] hover:bg-[#2F86B5] text-white" data-testid={`${testid}-forward`}>
              {forwardLabel} →
            </_Button>
          </Link>
        </_CardContent>
      </_Card>
    </div>
  );
}

export function CashDrillDownPage() {
  return <_DrillShell testid="drill-cash"
    title="Cash Balance"
    description="Balance of the Cash on Hand account (1000)."
    forwardLabel="Open Cashbook"
    forwardTo="/cashbook?account=1000&from=dashboard" />;
}
export function BankBalanceDrillDownPage() {
  return <_DrillShell testid="drill-bank"
    title="Bank Balance"
    description="Sum across all active bank accounts."
    forwardLabel="Open Bank & Cash"
    forwardTo="/bank?from=dashboard" />;
}
export function PayablesDrillDownPage() {
  return <_DrillShell testid="drill-payables"
    title="Payables Due"
    description="Outstanding supplier invoices not yet fully paid."
    forwardLabel="Open Accounts Payable"
    forwardTo="/payables?status=open&from=dashboard" />;
}
export function ReceivablesDrillDownPage() {
  return <_DrillShell testid="drill-receivables"
    title="Receivables Due"
    description="Outstanding customer invoices not yet received."
    forwardLabel="Open Accounts Receivable"
    forwardTo="/receivables?status=open&from=dashboard" />;
}
export function PayrollPendingDrillDownPage() {
  return <_DrillShell testid="drill-payroll-pending"
    title="Pending Payroll"
    description="Payroll runs in Draft or Approved status — not yet posted."
    forwardLabel="Open Payroll"
    forwardTo="/payroll?status=draft,approved&from=dashboard" />;
}

